// FileUploader.jsx
import React, { useState, useRef, useCallback } from 'react';
import { Form, Card, Button, Modal, Spinner } from 'react-bootstrap';
import './FileUploader.css';

import druggerUpload from '../../img/drugger.svg'; 

const FileUploader = ({
  label = "Завантажте фото чи скан-копію документа",
  description = "Один або кілька файлів у форматі PDF, JPG, PNG. Максимальний розмір файлу 2 МБ.",
  accept = ".pdf,.jpg,.jpeg,.png",
  multiple = false,
  maxSize = 2 * 1024 * 1024, // 2MB в байтах
  value = [],
  onChange,
  onBlur,
  onFocus,
  id,
  required = false,
  disabled = false,
  editable = true,
  asBase64 = true,
  maxCount = 5,
  errorVal,
  onFileViewRequest,
  onFileView,
  onFileDownload,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [errors, setErrors] = useState([]);
  const [showViewer, setShowViewer] = useState(false);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [loadingFile, setLoadingFile] = useState(false);
  const [loadedFiles, setLoadedFiles] = useState({});
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isZoomed, setIsZoomed] = useState(false);
  const fileInputRef = useRef(null);
  const imageRef = useRef(null);



  function readFileAsync(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        resolve(reader.result);
      };

      reader.onerror = (error) => {
        reject(error);
      };

      reader.readAsDataURL(file);
    });
  }

  const getFilesAsBase64 = async (files) => {
    if(!asBase64) return onChange(files);

    const result_files = [];
    for(let file of files) {
      const file_obj = { type: file.type, name: file.name, size: file.size};
      const content = await readFileAsync(file);
      file_obj['file'] = content;
      result_files.push(file_obj);
    }
    return result_files;
  }

  const returnFiles = async (files) => {
    const files_ = asBase64 ? await getFilesAsBase64(files) : files;
    const newValue = multiple ? [...value, ...files_] : [files_[0]];
    onChange(newValue);
  }

  const handleFileSelect = useCallback((files) => {
    const validFiles = [];
    const newErrors = [];

    Array.from(files).forEach((file) => {
      const validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
      if (!validTypes.includes(file.type)) {
        newErrors.push(`Файл "${file.name}" має недопустимий формат`);
        return;
      }

      if (file.size > maxSize) {
        newErrors.push(`Файл "${file.name}" перевищує максимальний розмір ${maxSize/1024/1024} МБ`);
        return;
      }

      validFiles.push(file);
    });

    if(validFiles.length > maxCount){
      newErrors.push(`Загальна кількість файлів перевищує ${maxCount} од.`);
      return;
    }

    setErrors(newErrors);

    if (validFiles.length > 0) {
      returnFiles(validFiles);
    }
  }, [value, multiple, maxSize, onChange, maxCount, asBase64]);

  // Функция для загрузки файла при просмотре
const loadFileForViewing = useCallback(async (fileIndex) => {
  const file = value[fileIndex];
  
  // Если файл уже загружен или это файл с сервера (имеет storage)
  if (file.file || file.content || (file.file && file.file.storage)) {
    return file.file || file.content;
  }

  // Если есть функция для загрузки файла и файл имеет _id
  if (onFileViewRequest && file._id) {
    setLoadingFile(true);
    try {
      const fileContent = await onFileViewRequest(file._id, fileIndex);
      
      // Обновляем кэш загруженных файлов
      setLoadedFiles(prev => ({
        ...prev,
        [fileIndex]: fileContent
      }));

      return fileContent;
    } catch (error) {
      console.error('Ошибка загрузки файла:', error);
      throw error;
    } finally {
      setLoadingFile(false);
    }
  }

  return null;
}, [value, onFileViewRequest]);

  // Функции для масштабирования
  const handleZoomIn = useCallback(() => {
    setZoomLevel(prev => {
      const newZoom = Math.min(prev + 0.25, 3);
      setIsZoomed(newZoom > 1);
      return newZoom;
    });
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoomLevel(prev => {
      const newZoom = Math.max(prev - 0.25, 0.5);
      setIsZoomed(newZoom > 1);
      return newZoom;
    });
  }, []);

  const handleZoomReset = useCallback(() => {
    setZoomLevel(1);
    setIsZoomed(false);
  }, []);

  const toggleZoom = useCallback(() => {
    if (isZoomed) {
      handleZoomReset();
    } else {
      setZoomLevel(1.5);
      setIsZoomed(true);
    }
  }, [isZoomed, handleZoomReset]);

  // Функции для просмотра файлов
const openFileViewer = useCallback(async (index) => {
  setCurrentFileIndex(index);
  setZoomLevel(1);
  setIsZoomed(false);
  
  const file = value[index];
  
  // Проверяем, нужно ли загружать файл
  // Для файлов с сервера (имеющих storage) не нужно загружать через onFileViewRequest
  const needsLoading = !file.file && !file.content && (file._id || onFileViewRequest);
  const isServerFile = file.file && file.file.storage; // Файл уже имеет метаданные с сервера
  
  if (needsLoading && !isServerFile) {
    setLoadingFile(true);
    try {
      await loadFileForViewing(index);
    } catch (error) {
      console.error('Ошибка при загрузке файла для просмотра:', error);
      setErrors(prev => [...prev, 'Не вдалося завантажити файл для перегляду']);
      return;
    } finally {
      setLoadingFile(false);
    }
  }
  
  setShowViewer(true);
  
  // Вызываем внешний обработчик просмотра файла
  if (onFileView && file) {
    onFileView(file);
  }
}, [value, loadFileForViewing, onFileViewRequest, onFileView]);

  const closeFileViewer = useCallback(() => {
    setShowViewer(false);
    setZoomLevel(1);
    setIsZoomed(false);
  }, []);

  const goToNextFile = useCallback(async () => {
    const nextIndex = (currentFileIndex + 1) % value.length;
    setCurrentFileIndex(nextIndex);
    setZoomLevel(1);
    setIsZoomed(false);
    
    // Предзагружаем следующий файл
    const nextFile = value[nextIndex];
    if (!nextFile.file && !nextFile.content && (nextFile._id || onFileViewRequest)) {
      loadFileForViewing(nextIndex);
    }
  }, [currentFileIndex, value.length, value, loadFileForViewing, onFileViewRequest]);

  const goToPrevFile = useCallback(async () => {
    const prevIndex = (currentFileIndex - 1 + value.length) % value.length;
    setCurrentFileIndex(prevIndex);
    setZoomLevel(1);
    setIsZoomed(false);
    
    // Предзагружаем предыдущий файл
    const prevFile = value[prevIndex];
    if (!prevFile.file && !prevFile.content && (prevFile._id || onFileViewRequest)) {
      loadFileForViewing(prevIndex);
    }
  }, [currentFileIndex, value.length, value, loadFileForViewing, onFileViewRequest]);

  // Получаем URL файла для отображения
const getFileUrl = useCallback((file) => {
  
  // Случай 1: Новый файл - file содержит base64 строку
  if (file.file && typeof file.file === 'string') {
    return file.file;
  }
  
  // Случай 2: Файл с сервера - file содержит объект с storage
  if (file.file && file.file.storage && file.file.storage.name) {
    // Генерируем URL для доступа к файлу через API
    const apiUrl = `${process.env.REACT_APP_API_URL}/files/preview/${file.file.storage.name}`;
    return apiUrl;
  }
  
  // Случай 3: Файл имеет прямую ссылку для preview
  if (file.previewUrl) {
    return file.previewUrl;
  }
  
  // Случай 4: Файл имеет content как base64
  if (file.content && typeof file.content === 'string') {
    return file.content;
  }
  
  // Случай 5: Файл загружен через onFileViewRequest
  if (loadedFiles[currentFileIndex]) {
    return loadedFiles[currentFileIndex];
  }
  
  //console.warn('No valid file URL found for file:', file);
  return null;
}, [loadedFiles, currentFileIndex]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files);
    }
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleInputChange = useCallback((e) => {
    const files = e.target.files;
    if (files.length > 0) {
      handleFileSelect(files);
    }
    // Сбрасываем значение input, чтобы можно было загрузить тот же файл снова
    e.target.value = '';
  }, [handleFileSelect]);

  const handleClickArea = useCallback(() => {
    if (!disabled && editable) {
      fileInputRef.current?.click();
    }
  }, [disabled, editable]);

  const removeFile = useCallback((index) => {
    const newValue = value.filter((_, i) => i !== index);
    
    // Удаляем из кэша
    setLoadedFiles(prev => {
      const newLoaded = { ...prev };
      delete newLoaded[index];
      // Сдвигаем индексы для файлов после удаленного
      Object.keys(newLoaded).forEach(key => {
        if (key > index) {
          newLoaded[key - 1] = newLoaded[key];
          delete newLoaded[key];
        }
      });
      return newLoaded;
    });
    
    onChange(newValue);
  }, [value, onChange]);

  const handleFileDownloadClick = useCallback((file, index) => {
    if (onFileDownload) {
      onFileDownload(file);
    } else {
      // Fallback: скачивание через создание ссылки
      const fileUrl = getFileUrl(file);
      if (fileUrl) {
        const a = document.createElement('a');
        a.href = fileUrl;
        a.download = file.name || 'download';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    }
  }, [onFileDownload, getFileUrl]);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const currentFile = value[currentFileIndex];
  const fileUrl = currentFile ? getFileUrl(currentFile) : null;
  const isImage = currentFile?.type?.includes('image');
  const isPDF = currentFile?.type === 'application/pdf';
  const hasFileContent = fileUrl || currentFile?.file || currentFile?.content;

  let allErrors = [...errors];
  if(errorVal) allErrors.push(errorVal);

  return (
    <Form.Group className="file-uploader-group">
      {label && (
        <Form.Label className="file-uploader-label">
          {label}
          {required && <span className="text-danger"> *</span>}
        </Form.Label>
      )}

      {/* Показываем зону загрузки только если editable = true */}
      {editable && (
        <div
          className={`file-drop-zone ${isDragging ? 'dragging' : ''} ${disabled ? 'disabled' : ''}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={handleClickArea}
        >
          <div className="drop-zone-content">
            <div className="upload-icon">
              <img src={druggerUpload} className="file-icon" alt="" aria-hidden="true" />
            </div>
            <p className="drop-zone-text">
              Натисніть або перетягніть файл у цю область для завантаження
            </p>
            <p className="drop-zone-description">
              {description}
            </p>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            multiple={multiple}
            onChange={handleInputChange}
            onBlur={onBlur}
            onFocus={onFocus}
            id={id}
            disabled={disabled}
            className="file-input-hidden"
          />
        </div>
      )}

      {/* Список загруженных файлов показываем всегда */}
      {value.length > 0 && (
        <div className="uploaded-files-list">
          {value.map((file, index) => (
            <Card key={index} className="uploaded-file-card">
              <Card.Body className="file-card-body">
                <div 
                  className="file-info clickable"
                  onClick={() => openFileViewer(index)}
                >
                  <div className="file-icon">
                    {file.type?.includes('image') ? (
                      <i className="bi bi-image"></i>
                    ) : (
                      <i className="bi bi-file-earmark-pdf"></i>
                    )}
                  </div>
                  <div className="file-details">
                    <div className="file-name">
                      {file.name}
                      {!file.file && !file.content && (file._id || file.file?._id) && (
                        <small className="text-muted ms-2">(потрібно завантажити для перегляду)</small>
                      )}
                    </div>
                    <div className="file-size">
                      {file.size ? formatFileSize(file.size) : 'Розмір невідомий'}
                    </div>
                  </div>
                </div>
                <div className="file-actions">
                  <button
                    className="btn btn-link action-link p-0 me-3"
                    onClick={() => openFileViewer(index)}
                    disabled={loadingFile}
                  >
                    <i className="bi bi-eye me-1"></i>
                    {loadingFile ? 'Завантаження...' : 'Переглянути'}
                  </button>
                  <button
                    className="btn btn-link action-link p-0 me-3"
                    onClick={() => handleFileDownloadClick(file, index)}
                  >
                    <i className="bi bi-download me-1"></i>
                    Завантажити
                  </button>
                  {/* Кнопку удаления показываем только если editable = true */}
                  {editable && !disabled && (
                    <button
                      className="btn btn-link action-link text-danger p-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFile(index);
                      }}
                    >
                      <i className="bi bi-trash me-1"></i>
                      Видалити
                    </button>
                  )}
                </div>
              </Card.Body>
            </Card>
          ))}
        </div>
      )}

      {/* Модальное окно просмотра файлов */}
      <Modal 
        show={showViewer} 
        onHide={closeFileViewer} 
        size="xl"
        centered
        className="file-viewer-modal"
        contentClassName="h-100"
      >
        <Modal.Header>
          <Modal.Title>
            {currentFile?.name} ({currentFileIndex + 1} з {value.length})
            {loadingFile && (
              <Spinner animation="border" size="sm" className="ms-2" />
            )}
          </Modal.Title>
          {/* Кастомная кнопка закрытия */}
          <button 
            className="btn-close-custom"
            onClick={closeFileViewer}
            aria-label="Close"
          >
            <i className="bi bi-x-lg"></i>
          </button>
        </Modal.Header>
        
        <Modal.Body>
          <div className="file-viewer-content">
            {loadingFile ? (
              <div className="file-loading">
                <Spinner animation="border" role="status" />
                <p className="mt-2">Завантаження файлу...</p>
              </div>
            ) : hasFileContent ? (
              <>
                {isImage && (
                  <>
                    <img 
                      ref={imageRef}
                      src={fileUrl} 
                      alt={currentFile?.name}
                      className={`viewer-image ${isZoomed ? 'zoomed' : 'zoomable'}`}
                      style={{ transform: `scale(${zoomLevel})` }}
                      onClick={toggleZoom}
                    />
                    
                    {/* Контролы масштабирования */}
                    <div className="viewer-controls">
                      <button 
                        className="control-btn" 
                        onClick={handleZoomOut}
                        disabled={zoomLevel <= 0.5}
                        title="Зменшити"
                      >
                        <i className="bi bi-dash-lg"></i>
                      </button>
                      
                      <span className="zoom-level">
                        {Math.round(zoomLevel * 100)}%
                      </span>
                      
                      <button 
                        className="control-btn" 
                        onClick={handleZoomReset}
                        disabled={zoomLevel === 1}
                        title="Скинути масштаб"
                      >
                        <i className="bi bi-arrow-clockwise"></i>
                      </button>
                      
                      <button 
                        className="control-btn" 
                        onClick={handleZoomIn}
                        disabled={zoomLevel >= 3}
                        title="Збільшити"
                      >
                        <i className="bi bi-plus-lg"></i>
                      </button>
                    </div>
                  </>
                )}
                {isPDF && (
                  <iframe
                    src={fileUrl}
                    title={currentFile?.name}
                    className="viewer-pdf"
                  />
                )}
                {!isImage && !isPDF && (
                  <div className="unsupported-file">
                    <i className="bi bi-file-earmark-x"></i>
                    <p>Перегляд цього типу файлів не підтримується</p>
                    <Button 
                      variant="primary" 
                      onClick={() => handleFileDownloadClick(currentFile, currentFileIndex)}
                    >
                      <i className="bi bi-download me-2"></i>
                      Завантажити файл
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <div className="file-not-loaded">
                <i className="bi bi-cloud-download"></i>
                <p>Файл не завантажений. Натисніть для перегляду.</p>
                <Button 
                  variant="primary" 
                  onClick={() => openFileViewer(currentFileIndex)}
                >
                  Завантажити для перегляду
                </Button>
              </div>
            )}
          </div>
        </Modal.Body>
        
        {value.length > 1 && (
          <Modal.Footer className="file-viewer-footer">
            <Button 
              variant="outline-secondary" 
              onClick={goToPrevFile}
              disabled={currentFileIndex === 0 || loadingFile}
            >
              <i className="bi bi-chevron-left"></i> Попередній
            </Button>
            <Button 
              variant="outline-secondary" 
              onClick={goToNextFile}
              disabled={currentFileIndex === value.length - 1 || loadingFile}
            >
              Наступний <i className="bi bi-chevron-right"></i>
            </Button>
          </Modal.Footer>
        )}
      </Modal>

      {/* Отображение ошибок */}
      {allErrors.length > 0 && (
        <div className="file-upload-errors">
          {allErrors.map((error, index) => (
            <div key={index} className="error-message">
              <i className="bi bi-exclamation-circle"></i>
              {error}
            </div>
          ))}
        </div>
      )}
    </Form.Group>
  );
};

export default FileUploader;